import React from 'react';
import { Link } from 'react-router-dom';
import { useLastRead } from '../hooks/useLastRead';
import { useBookmarks } from '../hooks/useBookmarks';
import { useNotes } from '../hooks/useNotes';

const formatTimestamp = (timestamp) => {
  const diff = Date.now() - timestamp;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

const SectionHeader = ({ children }) => (
  <h2
    className="text-xs uppercase tracking-widest font-bold mb-4"
    style={{ color: 'var(--text-secondary)' }}
  >
    {children}
  </h2>
);

const SavedScreen = () => {
  const { lastRead, hasLastRead } = useLastRead();
  const { bookmarks } = useBookmarks();
  const { notes } = useNotes();

  const isEmpty = !hasLastRead && bookmarks.length === 0 && notes.length === 0;

  if (isEmpty) {
    return (
      <div className="py-24 text-center">
        <div className="text-5xl mb-4">🔖</div>
        <p
          className="text-lg font-medium"
          style={{ color: 'var(--text-secondary)' }}
        >
          No saved items yet.
        </p>
        <p
          className="text-sm mt-2"
          style={{ color: 'var(--text-secondary)', opacity: 0.6 }}
        >
          Bookmark ayahs, add notes, or save your reading place.
        </p>
      </div>
    );
  }

  const resumeHref = lastRead
    ? lastRead.contextMode === 'juz'
      ? `/juz/${lastRead.juzNumber}?ayah=${lastRead.ayahNumber}&mode=${lastRead.readingMode}`
      : `/surah/${lastRead.surahNumber}?ayah=${lastRead.ayahNumber}&mode=${lastRead.readingMode}`
    : null;

  const resumeLabel = lastRead
    ? lastRead.contextMode === 'juz'
      ? `Juz ${lastRead.juzNumber} · Ayah ${lastRead.ayahNumber}`
      : `Surah ${lastRead.surahNumber} · Ayah ${lastRead.ayahNumber}`
    : null;

  // Sort newest first
  const sortedBookmarks = [...bookmarks].sort((a, b) => b.timestamp - a.timestamp);
  const sortedNotes = [...notes].sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div className="space-y-10">

      {/* ── Resume Reading ── */}
      {hasLastRead && lastRead && (
        <section>
          <SectionHeader>Resume Reading</SectionHeader>
          <Link
            to={resumeHref}
            className="flex items-center gap-4 p-4 rounded-xl transition-all active:scale-[0.98]"
            style={{
              background: 'var(--gold-muted)',
              border: '2px solid var(--border-gold)',
              boxShadow: 'var(--shadow-sm)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(212, 175, 55, 0.18)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--gold-muted)';
            }}
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'var(--bg-elevated)',
                border: '2px solid var(--border-gold)',
              }}
            >
              <span className="text-xl">📖</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm" style={{ color: 'var(--gold-hover)' }}>
                Continue where you left off
              </p>
              <p className="text-sm mt-0.5 truncate" style={{ color: 'var(--text-secondary)' }}>
                {resumeLabel}
              </p>
            </div>
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: 'var(--gold-hover)' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </section>
      )}

      {/* ── Bookmarks ── */}
      {sortedBookmarks.length > 0 && (
        <section>
          <SectionHeader>Bookmarks · {sortedBookmarks.length}</SectionHeader>
          <div className="space-y-2">
            {sortedBookmarks.map((bm) => {
              const to = bm.ayahNumber
                ? `/surah/${bm.surahNumber}?ayah=${bm.ayahNumber}&mode=scroll`
                : `/surah/${bm.surahNumber}`;

              return (
                <Link
                  key={bm.id}
                  to={to}
                  className="flex items-start gap-3 p-4 rounded-xl transition-all"
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-default)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-gold)';
                    e.currentTarget.style.background = 'rgba(212, 175, 55, 0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-default)';
                    e.currentTarget.style.background = 'var(--bg-secondary)';
                  }}
                >
                  <span className="text-lg flex-shrink-0 mt-0.5">🔖</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--gold-hover)' }}>
                      {bm.surahEnglishName}
                      {bm.ayahNumber ? (
                        <span className="font-normal ml-1" style={{ color: 'var(--text-secondary)' }}>
                          · Ayah {bm.ayahNumber}
                        </span>
                      ) : null}
                    </p>
                    {bm.ayahText && (
                      <p
                        className="text-sm mt-1 line-clamp-2"
                        style={{ color: 'var(--text-english)' }}
                      >
                        {bm.ayahText}
                      </p>
                    )}
                    <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                      {formatTimestamp(bm.timestamp)}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Notes ── */}
      {sortedNotes.length > 0 && (
        <section>
          <SectionHeader>Notes · {sortedNotes.length}</SectionHeader>
          <div className="space-y-2">
            {sortedNotes.map((note) => (
              <Link
                key={note.id}
                to={`/surah/${note.surahNumber}?ayah=${note.ayahNumber}&mode=scroll`}
                className="flex items-start gap-3 p-4 rounded-xl transition-all"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)';
                  e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-default)';
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                }}
              >
                <span className="text-lg flex-shrink-0 mt-0.5">📝</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-english)' }}>
                    {note.surahEnglishName}
                    <span className="font-normal ml-1" style={{ color: 'var(--text-secondary)' }}>
                      · Ayah {note.ayahNumber}
                    </span>
                  </p>
                  {note.content && (
                    <p
                      className="text-sm mt-1 line-clamp-2"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {note.content}
                    </p>
                  )}
                  <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    {formatTimestamp(note.timestamp)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

    </div>
  );
};

export default SavedScreen;
